/**
 * cloudImportDedup.ts — pure guardrails against duplicate Forge Cloud projects.
 *
 * Forge Cloud imports create a CMMD control-plane project and then a runtime
 * keyed on that project id, so every redundant CMMD create becomes a permanent
 * duplicate row in the CLOUD sidebar group. The GitHub import path already
 * dedups through CMMD's repo-keyed `ensure` endpoint; the blank-name and
 * Blank-workspace creation calls the plain create endpoint, so the client must refuse to
 * re-create a cloud project whose name already exists in the runtime catalog.
 *
 * Matching is case-insensitive on the trimmed display name because the CMMD
 * create endpoint has no uniqueness constraint — "cmmd" and "CMMD" would
 * otherwise become distinct projects with indistinguishable sidebar rows.
 */

import type { CmmdProject } from "@cmmd-forge/contracts";

import type { CloudProjectCatalogRow } from "../cmmd/cmmdForgeRuntimeListResource";

/**
 * Where the repair affordance lives, named in one place so import copy and the
 * button it points at cannot drift apart.
 *
 * Re-import is register-only for a project CMMD already knows about: it writes a
 * cloud registration row and returns. It does not provision, connect,
 * materialize the workspace inside the sprite, or rewrite the CMMD project link
 * — so telling a user to "import it again" when their workspace is broken sends
 * them down a path that provably cannot fix it (verified 2026-08-18 on CMMD
 * project 198). Point at the action that actually rebuilds instead.
 */
export const REPAIR_ACTION_REFERENCE = "Repair project in Settings > Connections";

/**
 * The already-registered cloud row for a CMMD project, if any.
 *
 * Keyed on the CMMD project id rather than the display name: this answers
 * "has this exact project been imported before", which is what decides whether
 * an import is a first import or a no-op re-import. The name-based
 * {@link findDuplicateCloudProjectRow} answers a different question (would this
 * NEW create collide), and the two must not be conflated.
 */
export function findRegisteredCloudProjectRow<
  Row extends { readonly cmmdProjectId: number; readonly status: string },
>(rows: readonly Row[], cmmdProjectId: number): Row | null {
  return (
    rows.find((row) => row.status !== "destroyed" && row.cmmdProjectId === cmmdProjectId) ?? null
  );
}

/**
 * What actually happened, said plainly.
 *
 * The import job used to report "Project ready" for every cloud import,
 * including a re-import that did nothing but rewrite a registration row. That
 * reads as "your project was rebuilt" to a user whose workspace is broken,
 * which is the opposite of true.
 */
export function describeCloudProjectImportOutcome(input: {
  readonly alreadyRegistered: boolean;
  readonly hasRuntime: boolean;
}): string {
  if (!input.alreadyRegistered) {
    return "Added to Forge Cloud. Your project starts when you open the first thread.";
  }
  return input.hasRuntime
    ? `Already in Forge Cloud. Re-importing does not rebuild it \u2014 use ${REPAIR_ACTION_REFERENCE} if it is not working.`
    : "Already in Forge Cloud. Re-importing does not rebuild it \u2014 open a thread to start the workspace.";
}

export type CloudProjectNameSlice = Pick<
  CloudProjectCatalogRow,
  "runtimeId" | "projectName" | "status"
>;

function normalizeCloudProjectName(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * Find an existing (non-destroyed) cloud project row whose display name
 * matches `name` case-insensitively. Destroyed runtimes are ignored — the
 * user deliberately removed them, so re-importing under that name is a
 * legitimate recreate, not a duplicate.
 */
export function findDuplicateCloudProjectRow<Row extends CloudProjectNameSlice>(
  rows: readonly Row[],
  name: string,
): Row | null {
  const normalized = normalizeCloudProjectName(name);
  if (normalized.length === 0) return null;
  return (
    rows.find(
      (row) =>
        row.status !== "destroyed" && normalizeCloudProjectName(row.projectName) === normalized,
    ) ?? null
  );
}

/**
 * How loudly a panel notice should speak.
 *
 * `blocked` is a precondition the user can resolve: nothing failed and nothing
 * was destroyed. `error` is reserved for work that actually went wrong. The
 * distinction exists because destructive styling only means something while it
 * stays rare — spending red on a healthy workspace devalues it for a real
 * failure two screens later.
 */
export type ProjectSourceNoticeTone = "blocked" | "error";

export interface ProjectSourceNotice {
  readonly tone: ProjectSourceNoticeTone;
  /** The fact, on its own. Short enough to read without scanning. */
  readonly title: string;
  /** What to do about it. Ranked below the title, never merged into it. */
  readonly detail: string;
}

/**
 * Notice for a blocked duplicate cloud import, structured rather than flat.
 *
 * A single sentence carrying the fact, two remedies and a caveat renders as
 * four equal-weight lines, which is four things shouting and none of them
 * first. Splitting title from detail is what lets the panel rank them.
 * Includes the existing row's exact spelling so a case-variant collision
 * ("CMMD" vs "cmmd") is explainable at a glance.
 */
export function duplicateCloudProjectImportNotice(
  name: string,
  existingName: string,
): ProjectSourceNotice {
  const suffix =
    normalizeCloudProjectName(name) === normalizeCloudProjectName(existingName) &&
    name.trim() !== existingName.trim()
      ? ` (as "${existingName.trim()}")`
      : "";
  return {
    tone: "blocked",
    title: `"${name.trim()}" already exists in Forge Cloud${suffix}`,
    detail: `Open it from the Cloud section, or use ${REPAIR_ACTION_REFERENCE} if it is not working. Re-importing does not rebuild it.`,
  };
}

// ── Blank-create plan ─────────────────────────────────────────────────────────

/** The CMMD control-plane project context a blank cloud import runs against. */
export interface BlankCloudCmmdProjectContext {
  readonly project: CmmdProject;
  readonly organizationId: number;
  readonly campaignName: string | null;
}

/**
 * What a blank-name Forge Cloud import should do for a given name:
 * - `resume`: a prior attempt already created the CMMD project — reuse it.
 * - `block`: a live cloud project already answers to this name — refuse to
 *   create a duplicate.
 * - `create`: no prior attempt and no name collision — create fresh.
 */
export type BlankCloudImportPlan =
  | { readonly kind: "create" }
  | { readonly kind: "resume"; readonly cmmdProject: BlankCloudCmmdProjectContext }
  | {
      readonly kind: "block";
      /** Ranked form, for panels that can present a title and a detail. */
      readonly notice: ProjectSourceNotice;
    };

/**
 * Resolve the plan for a blank cloud import. Recovery order:
 * 1. Same-mount draft state (exact name match).
 * 2. The persisted import job's pendingCmmdContext (survives reloads; matched
 *    by job id, which is already case-insensitive on the name).
 * 3. Duplicate-name guard against the cloud runtime catalog.
 * Non-cloud destinations always plan `create` — local imports have no CMMD
 * control-plane project to duplicate.
 */
export function resolveBlankCloudImportPlan(input: {
  readonly cloudDestination: boolean;
  readonly name: string;
  readonly jobId: string;
  readonly draftPending: {
    readonly draftName: string;
    readonly cmmdProject: BlankCloudCmmdProjectContext;
  } | null;
  readonly jobs: ReadonlyArray<{
    readonly id: string;
    readonly pendingCmmdContext: BlankCloudCmmdProjectContext | null;
  }>;
  readonly rows: readonly CloudProjectNameSlice[];
}): BlankCloudImportPlan {
  if (!input.cloudDestination) return { kind: "create" };
  if (input.draftPending !== null && input.draftPending.draftName === input.name) {
    return { kind: "resume", cmmdProject: input.draftPending.cmmdProject };
  }
  const persisted = input.jobs.find((job) => job.id === input.jobId)?.pendingCmmdContext ?? null;
  if (persisted !== null) {
    return {
      kind: "resume",
      cmmdProject: {
        project: persisted.project,
        organizationId: persisted.organizationId,
        campaignName: persisted.campaignName,
      },
    };
  }
  const duplicateRow = findDuplicateCloudProjectRow(input.rows, input.name);
  if (duplicateRow !== null) {
    return {
      kind: "block",
      notice: duplicateCloudProjectImportNotice(input.name, duplicateRow.projectName),
    };
  }
  return { kind: "create" };
}
